import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Reveal } from './Reveal';

export default function HoverRevealList({ items, testIdPrefix }) {
  const [active, setActive] = useState(null);
  const [tapped, setTapped] = useState(null);
  const imgRef = useRef(null);
  const finePointer = typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches;

  const onMove = (e) => {
    if (imgRef.current) {
      imgRef.current.style.transform = `translate(${e.clientX + 24}px, ${e.clientY - 150}px)`;
    }
  };

  const onRowClick = (i) => {
    if (!finePointer) setTapped(tapped === i ? null : i);
  };

  return (
    <div onMouseMove={onMove} className="relative" data-testid={`${testIdPrefix}-list`}>
      {items.map((item, i) => (
        <Reveal key={i} delay={i * 0.06}>
          <div
            role={finePointer ? undefined : 'button'}
            tabIndex={finePointer ? undefined : 0}
            aria-expanded={finePointer ? undefined : tapped === i}
            className="group relative border-t border-zinc-900 py-8 transition-colors duration-500 last:border-b hover:bg-zinc-950 sm:py-10"
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(null)}
            onClick={() => onRowClick(i)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(i); } }}
            data-testid={`${testIdPrefix}-item-${i}`}
          >
            <div className="mx-auto flex max-w-[1600px] flex-col gap-3 px-5 sm:flex-row sm:items-center sm:gap-10 sm:px-8">
              <span className="font-mono text-sm text-zinc-600 transition-colors duration-300 group-hover:text-[var(--brand)]">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="font-heading text-3xl font-extrabold uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-[var(--brand)] sm:text-5xl">
                {item.title}
              </h3>
              {item.badge && (
                <span className="inline-flex w-fit items-center gap-2 border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] brand-border brand-text" data-testid={`${testIdPrefix}-badge-${i}`}>
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full brand-bg" />
                  {item.badge}
                </span>
              )}
              {item.desc && (
                <p className="max-w-md text-sm leading-relaxed text-zinc-500 sm:ml-auto">{item.desc}</p>
              )}
              <ArrowUpRight className="hidden h-6 w-6 shrink-0 text-zinc-700 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[var(--brand)] sm:block" />
            </div>

            {!finePointer && (
              <AnimatePresence initial={false}>
                {tapped === i && item.img && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="mx-5 mt-5 aspect-[16/10] overflow-hidden sm:mx-8">
                      <img src={item.img} alt={item.title} loading="lazy" className="h-full w-full object-cover" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>
        </Reveal>
      ))}

      {finePointer && (
        <div
          ref={imgRef}
          aria-hidden="true"
          className="pointer-events-none fixed left-0 top-0 z-[80] h-[260px] w-[380px] overflow-hidden transition-opacity duration-300"
          style={{ opacity: active !== null && items[active]?.img ? 1 : 0 }}
        >
          {items.map((item, i) =>
            item.img ? (
              <img
                key={i}
                src={item.img}
                alt=""
                loading="lazy"
                className={`absolute inset-0 h-full w-full object-cover transition-all duration-500 ease-out ${
                  active === i ? 'scale-100 opacity-100' : 'scale-[1.06] opacity-0'
                }`}
              />
            ) : null
          )}
          <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
        </div>
      )}
    </div>
  );
}
